import { useInView } from '../../hooks/useInView';
import { MessageSquare, Camera, BarChart3, Target, Bell, CheckCircle2, Mic } from 'lucide-react';

const items = [
  { icon: MessageSquare, title: 'Registre gastos por texto ou voz', description: 'Envie "Gastei 50 no mercado" ou grave um áudio. A IA transcreve e registra a despesa automaticamente.' },
  { icon: Camera, title: 'Envie fotos de recibos e notas fiscais', description: 'Fotografe o comprovante e envie pelo WhatsApp. A IA com GPT-4o analisa e extrai valor, descrição e categoria automaticamente.' },
  { icon: BarChart3, title: 'Consulte saldo, extrato e metas', description: 'Pergunte "Qual meu saldo?" ou "Minhas metas" e receba um resumo financeiro completo instantaneamente.' },
  { icon: Target, title: 'Crie metas e investimentos por mensagem', description: 'Envie "Criar meta viagem de 5000 reais" ou "Investi 1000 em ações" e gerencie tudo sem abrir o app.' },
  { icon: Bell, title: 'Resumo diário automático no seu horário', description: 'Configure o horário e receba todo dia um resumo das tarefas e compromissos financeiros direto no WhatsApp.' },
];

function ChatMockup() {
  return (
    <div className="relative mx-auto max-w-sm">
      <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl" />
      <div className="relative rounded-3xl border border-border/70 bg-card shadow-2xl overflow-hidden">
        {/* Chat header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-emerald-600 text-white">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold">A</div>
          <div>
            <div className="text-sm font-semibold">Aretê Bot</div>
            <div className="text-xs opacity-80">online</div>
          </div>
        </div>

        {/* Chat body */}
        <div className="p-4 space-y-3 bg-[url('data:image/svg+xml;utf8,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%224%22%20height=%224%22%3E%3Crect%20width=%224%22%20height=%224%22%20fill=%22%23f0f2f5%22/%3E%3C/svg%3E')] bg-muted/40">
          {/* User */}
          <div className="flex justify-end">
            <div className="bg-emerald-100 text-emerald-950 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[80%] text-sm">
              Qual meu saldo?
            </div>
          </div>
          {/* Bot */}
          <div className="flex justify-start">
            <div className="bg-white text-foreground rounded-2xl rounded-tl-sm px-3 py-2.5 max-w-[85%] text-sm shadow-sm border border-border/40">
              <div className="font-semibold mb-1">Seu Resumo Financeiro:</div>
              <div className="text-xs space-y-0.5">
                <div>💰 Saldo: <span className="font-semibold text-emerald-600">R$ 3.450,00</span></div>
                <div>📈 Receitas: R$ 5.000,00</div>
                <div>📉 Despesas: R$ 1.550,00</div>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5">Atualizado agora</div>
            </div>
          </div>

          {/* Audio */}
          <div className="flex justify-end">
            <div className="bg-emerald-100 text-emerald-950 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%] text-sm flex items-center gap-2">
              <Mic className="w-4 h-4" />
              <span className="text-xs">Áudio 0:05</span>
              <span className="text-xs italic opacity-80">&quot;Gastei oitenta e cinco reais no restaurante&quot;</span>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white text-foreground rounded-2xl rounded-tl-sm px-3 py-2.5 max-w-[85%] text-sm shadow-sm border border-border/40">
              <div className="flex items-center gap-1.5 text-emerald-600 mb-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="font-semibold">Despesa registrada!</span>
              </div>
              <div className="text-xs">R$ 85,00 · Restaurante</div>
              <div className="text-xs text-muted-foreground">Novo saldo: R$ 3.365,00</div>
            </div>
          </div>

          {/* Goal */}
          <div className="flex justify-end">
            <div className="bg-emerald-100 text-emerald-950 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[80%] text-sm">
              Criar meta viagem 5000
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white text-foreground rounded-2xl rounded-tl-sm px-3 py-2.5 max-w-[85%] text-sm shadow-sm border border-border/40">
              <div className="flex items-center gap-1.5 text-emerald-600 mb-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="font-semibold">Meta criada com sucesso!</span>
              </div>
              <div className="text-xs">🎯 viagem · R$ 5.000,00</div>
            </div>
          </div>
        </div>

        {/* Chat input */}
        <div className="px-3 py-2.5 border-t border-border/40 bg-muted/60 text-xs text-muted-foreground">
          Digite uma mensagem...
        </div>
      </div>
    </div>
  );
}

export default function WhatsappSection() {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section className="relative py-32 overflow-hidden" data-testid="whatsapp-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_30%_50%,hsl(var(--primary)/0.08),transparent)]" />

      <div className="relative mx-auto max-w-7xl px-6" ref={ref}>
        <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Disponível Agora
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
            Gerencie suas finanças pelo WhatsApp
          </h2>
          <p className="text-muted-foreground text-lg">
            Nosso bot com inteligência artificial já está ativo e pronto para simplificar sua vida financeira. Converse naturalmente, envie áudios e fotos — o bot entende tudo.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Feature list */}
          <div className="space-y-5">
            {items.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`flex gap-4 transition-all duration-700 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`transition-all duration-1000 ${isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <ChatMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
